import classnames from 'classnames';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Button from 'components/Button';
import { Container, Grid, Cell } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import MediaGallery from 'components/MediaGallery';
import { Small, MediumUp } from 'components/MediaQuery';
import { medium, mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import RichTextProcessor from 'utils/RichTextProcessor';

class PageSectionImageCopy extends PureComponent {
  constructor(props) {
    super(props);

    this.state = { activeMediaGalleryIndex: 0 };
  }

  setActiveMediaGalleryIndex(index) {
    this.setState({ activeMediaGalleryIndex: index });
  }

  render() {
    const { content, isFlipped, hasBackgroundColor, forwardedRef, className, ...otherProps } =
      this.props;
    const classes = classnames('PageSectionImageCopy', className, {
      'PageSectionImageCopy--isFlipped': isFlipped,
      'PageSectionImageCopy--hasBackgroundColor': hasBackgroundColor,
    });

    const processedSubtext = new RichTextProcessor(content.subtext).getReactComponents();

    return (
      <div className={classes} ref={forwardedRef} {...otherProps}>
        <MediumUp>
          <Grid className="PageSectionImageCopy__fill-grid">
            <Cell mediumUp={{ columns: 6 }}>&nbsp;{/* layout placeholder */}</Cell>
            <Cell mediumUp={{ columns: 6 }}>
              <div className="PageSectionImageCopy__media-gallery-container">
                <MediaGallery
                  media={
                    content.desktopImages || [
                      content.desktopImage,
                    ] /* need to DRY up content model */
                  }
                  onIndexChange={index => this.setActiveMediaGalleryIndex(index)}
                  activeIndex={this.state.activeMediaGalleryIndex}
                />
              </div>
            </Cell>
          </Grid>
        </MediumUp>

        <Container>
          <Grid className="PageSectionImageCopy__content-grid">
            <Cell small={{ columns: 12 }} mediumUp={{ columns: 6 }}>
              <div className="PageSectionImageCopy__content">
                <div className="PageSectionImageCopy__content-inner">
                  <SecondaryHeading>{content.heading}</SecondaryHeading>

                  {processedSubtext}

                  <Grid className="PageSectionImageCopy__content-grid-cell">
                    {map(content.ctaLinks, ({ text, url, openInNewWindow }, index) => {
                      const hasMultipleLinks = get(content.ctaLinks, 'length') > 0;
                      const addRightMargin = hasMultipleLinks && index === 0;
                      const addLeftMargin = hasMultipleLinks && index === 1;
                      const addBottomMargin = hasMultipleLinks && index === 0;

                      const linkContainerClasses = classnames(
                        'PageSectionImageCopy__content-link-container',
                        {
                          'PageSectionImageCopy__content-link-container--add-bottom-margin':
                            addBottomMargin,
                          'PageSectionImageCopy__content-link-container--add-right-margin':
                            addRightMargin,
                          'PageSectionImageCopy__content-link-container--add-left-margin':
                            addLeftMargin,
                        }
                      );

                      return (
                        <Cell
                          small={{ columns: 12 }}
                          medium={{ columns: 12 }}
                          largeUp={{ columns: 6 }}
                          key={`cta-link-${index}`}
                        >
                          <div className={linkContainerClasses}>
                            <Button linkTo={url} target={openInNewWindow && '_blank'} isFullWidth>
                              {text}
                            </Button>
                          </div>
                        </Cell>
                      );
                    })}
                  </Grid>
                </div>
              </div>
            </Cell>

            <Cell small={{ columns: 12 }} mediumUp={{ columns: 6 }}>
              <Small>
                <div className="PageSectionImageCopy__media-gallery-container">
                  <MediaGallery
                    media={
                      content.mobileImages || [
                        content.mobileImage,
                      ] /* need to DRY up content model */
                    }
                    onIndexChange={index => this.setActiveMediaGalleryIndex(index)}
                    activeIndex={this.state.activeMediaGalleryIndex}
                  />
                </div>
              </Small>
            </Cell>
          </Grid>
        </Container>

        <style jsx>
          {`
            .PageSectionImageCopy {
              position: relative;
              overflow: hidden;
            }

            .PageSectionImageCopy.PageSectionImageCopy--hasBackgroundColor {
              background-color: ${Theme.get('colors-grey')};
            }

            .PageSectionImageCopy__content {
              display: flex;
              position: relative;
              justify-content: center;
              height: 100%;
              z-index: 3;
            }

            .PageSectionImageCopy__content-inner {
              padding: ${Theme.get('spacing') * 6}px 0;
            }

            .PageSectionImageCopy__content-link-container--add-bottom-margin {
              margin-bottom: ${Theme.get('spacing') * 4}px;
            }
          `}
        </style>

        <style jsx global>
          {`
            .PageSectionImageCopy__fill-grid {
              position: absolute;
              top: 0;
              right: 0;
            }

            .PageSectionImageCopy .PageSectionImageCopy__content .UnorderedList {
              margin-bottom: ${Theme.get('spacing') * 6}px;
            }

            .PageSectionImageCopy__content .UnorderedList .Paragraph {
              margin: 0;
            }

            .PageSectionImageCopy__content-inner .typography--secondary-heading {
              margin-bottom: ${Theme.get('spacing') * 3}px;
            }

            .PageSectionImageCopy .PageSectionImageCopy__content-grid {
              flex-direction: column-reverse;
            }

            .PageSectionImageCopy .MediaGallery {
              margin: 0 -${Theme.get('spacing') * 6}px;
            }

            @media ${medium} {
              .PageSectionImageCopy .MediaGallery {
                margin: 0;
              }

              .PageSectionImageCopy .PageSectionImageCopy__content-inner {
                padding: 0;
              }
            }

            @media ${mediumUp} {
              .PageSectionImageCopy {
                display: flex;
              }
              .PageSectionImageCopy .MediaGallery {
                margin: 0;
              }

              .PageSectionImageCopy .PageSectionImageCopy__content-grid {
                flex-direction: row;
                justify-content: center;
                height: 100%;
              }

              .PageSectionImageCopy--isFlipped .PageSectionImageCopy__content-grid {
                flex-direction: row-reverse;
              }

              .PageSectionImageCopy--isFlipped .PageSectionImageCopy__fill-grid {
                flex-direction: row-reverse;
              }

              .PageSectionImageCopy .PageSectionImageCopy__content {
                display: flex;
                justify-content: center;
                flex-direction: column;
                min-height: ${Theme.get('page-height-px')};
                padding: 0 ${Theme.get('spacing') * 4}px 0 ${Theme.get('spacing') * 2}px;
              }

              .PageSectionImageCopy--isFlipped .PageSectionImageCopy__content {
                padding: 0 ${Theme.get('spacing') * 2}px 0 ${Theme.get('spacing') * 4}px;
              }

              .PageSectionImageCopy .AspectRatio {
                min-height: ${Theme.get('page-height-px')};
                padding-bottom: 0;
              }
            }

            @media ${largeUp} {
              .PageSectionImageCopy__content-link-container--add-right-margin {
                margin-right: ${Theme.get('spacing') * 2}px;
              }

              .PageSectionImageCopy__content-link-container--add-left-margin {
                margin-left: ${Theme.get('spacing') * 2}px;
              }

              .PageSectionImageCopy .PageSectionImageCopy__content {
                padding: 0 ${Theme.get('spacing') * 22}px 0 ${Theme.get('spacing') * 8}px;
              }

              .PageSectionImageCopy--isFlipped .PageSectionImageCopy__content {
                padding: 0 ${Theme.get('spacing') * 8}px 0 ${Theme.get('spacing') * 22}px;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

PageSectionImageCopy.propTypes = {
  className: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
  isFlipped: PropTypes.bool,
  hasBackgroundColor: PropTypes.bool,
  content: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subtext: PropTypes.shape({}).isRequired,
    ctaLinks: PropTypes.arrayOf(
      PropTypes.shape({
        _contentType: PropTypes.string.isRequired,
        internalName: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        openInNewWindow: PropTypes.bool.isRequired,
      })
    ),
    desktopImages: PropTypes.arrayOf(
      PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      })
    ),
    desktopImage: PropTypes.shape({
      file: PropTypes.shape({
        cloudinaryPublicId: PropTypes.string,
        details: PropTypes.shape({
          image: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
          }),
        }),
      }),
    }),
    mobileImages: PropTypes.arrayOf(
      PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      })
    ),
    mobileImage: PropTypes.shape({
      file: PropTypes.shape({
        cloudinaryPublicId: PropTypes.string,
        details: PropTypes.shape({
          image: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
          }),
        }),
      }),
    }),
  }).isRequired,
};

PageSectionImageCopy.defaultProps = {
  className: '',
  forwardedRef: null,
  isFlipped: false,
  hasBackgroundColor: false,
};

export default React.forwardRef((props, ref) => {
  return <PageSectionImageCopy {...props} forwardedRef={ref} />;
});
